import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link, useParams } from "react-router-dom";
import { registerInvestor, registerStaff, validateInvitationCode } from "@/services/authService";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { LogIn, Loader2, ArrowLeft, Mail, Lock, User, Phone, Globe, MapPin, Flag, Calendar, CheckCircle, AlertCircle, Users, Briefcase } from "lucide-react";

const ACCOUNT_TYPES = {
  investor: {
    icon: Briefcase,
    title: "investor_account",
    desc: "investor_desc",
    color: "from-blue-500/20 to-blue-600/10",
    borderColor: "border-blue-500/30",
  },
  staff: {
    icon: Users,
    title: "staff_account",
    desc: "staff_desc",
    color: "from-purple-500/20 to-purple-600/10",
    borderColor: "border-purple-500/30",
  },
};

export default function RegisterPage() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState("type"); // type | form
  const [accountType, setAccountType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    passwordConfirm: "",
    fullName: "",
    phone: "",
    // Yatırımcı için
    country: "",
    city: "",
    nationality: "",
    dateOfBirth: "",
    // Personel için
    invitationCode: "",
  });

  const [invitationValidating, setInvitationValidating] = useState(false);
  const [invitationValid, setInvitationValid] = useState(null);

  const handleTypeSelect = (type) => {
    setAccountType(type);
    setStep("form");
  };

  const handleBackToTypes = () => {
    setStep("type");
    setAccountType(null);
    setError("");
    setInvitationValid(null);
  };

  const handleInvitationCheck = async (code) => {
    if (!code || code.length < 4) {
      setInvitationValid(null);
      return;
    }

    setInvitationValidating(true);
    try {
      const result = await validateInvitationCode(code);
      setInvitationValid(result.valid);
      if (!result.valid) {
        setError(t("auth.invalid_invitation"));
      } else {
        setError("");
      }
    } catch (err) {
      setInvitationValid(false);
    } finally {
      setInvitationValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validasyon
    if (formData.password !== formData.passwordConfirm) {
      setError(t("auth.passwords_do_not_match"));
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t("auth.weak_password"));
      setLoading(false);
      return;
    }

    try {
      if (accountType === "investor") {
        await registerInvestor({
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName,
          phone: formData.phone,
          country: formData.country,
          city: formData.city,
          nationality: formData.nationality,
          dateOfBirth: formData.dateOfBirth,
        });
      } else if (accountType === "staff") {
        if (!invitationValid) {
          setError(t("auth.invalid_invitation"));
          setLoading(false);
          return;
        }
        await registerStaff({
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName,
          phone: formData.phone,
          invitationCode: formData.invitationCode,
        });
      }

      toast({ title: t("auth.register_success") });
      navigate(`/${lang}/giris`);
    } catch (err) {
      setError(err.message || t("auth.register_error"));
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "invitationCode") {
      setError("");
      handleInvitationCheck(value);
    }
  };

  // Hesap tipi seçimi ekranı
  if (step === "type") {
    return (
      <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4 relative overflow-hidden">
        {/* Dekoratif arka plan */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#D4AF37]/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        </div>

        <Link
          to={`/${lang}`}
          className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors text-sm z-10"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("header.properties") ? "Ana Sayfa" : "Home"}
        </Link>

        <div className="relative z-10 w-full max-w-2xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1
              className="text-3xl font-bold text-white mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Oly<span className="text-[#D4AF37]">Investment</span>
            </h1>
            <p className="text-white/50 text-sm">KKTC Gayrimenkul & Yatirim</p>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-white mb-2">
              {t("auth.choose_account_type")}
            </h2>
            <p className="text-white/50 text-sm">
              Size en uygun hesap turunu secin
            </p>
          </div>

          {/* Hesap tipi kartları */}
          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(ACCOUNT_TYPES).map(([type, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={type}
                  onClick={() => handleTypeSelect(type)}
                  className={`group relative p-6 rounded-xl border transition-all duration-300 hover:scale-[1.02] ${config.borderColor} ${config.color}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${config.color} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-white font-semibold mb-1">
                        {t(`auth.${config.title}`)}
                      </h3>
                      <p className="text-white/50 text-sm">
                        {t(`auth.${config.desc}`)}
                      </p>
                    </div>
                  </div>
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <ArrowLeft className="w-4 h-4 text-[#D4AF37] rotate-180" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <p className="text-center text-white/30 text-sm mt-8">
            {t("auth.has_account")}{" "}
            <Link to={`/${lang}/giris`} className="text-[#D4AF37] hover:text-[#B5952F]">
              {t("auth.login")}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Kayıt formu ekranı
  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4 py-8 relative overflow-hidden">
      {/* Dekoratif arka plan */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#D4AF37]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      </div>

      <button
        onClick={handleBackToTypes}
        className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors text-sm z-10"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("auth.choose_account_type")}
      </button>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-6">
          <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${ACCOUNT_TYPES[accountType].color} mb-3`}>
            {ACCOUNT_TYPES[accountType].icon && (
              React.createElement(ACCOUNT_TYPES[accountType].icon, { className: "w-6 h-6 text-[#D4AF37]" })
            )}
          </div>
          <h2 className="text-xl font-semibold text-white">
            {t(`auth.${ACCOUNT_TYPES[accountType].title}`)}
          </h2>
        </div>

        <Card className="bg-white/[0.03] border-white/10 backdrop-blur-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg">
              {t("auth.register")}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Ad Soyad */}
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-white/70 text-sm">
                  {t("auth.full_name")}
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    id="fullName"
                    placeholder="Ad Soyad"
                    value={formData.fullName}
                    onChange={(e) => updateField("fullName", e.target.value)}
                    required
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70 text-sm">
                  {t("auth.email")}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    required
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
              </div>

              {/* Telefon */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white/70 text-sm">
                  {t("auth.phone")}
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    id="phone"
                    placeholder="+90 533 123 45 67"
                    value={formData.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
              </div>

              {/* Yatırımcı alanları */}
              {accountType === "investor" && (
                <>
                  <Separator className="bg-white/10 my-4" />
                  <div className="space-y-4">
                    <p className="text-white/50 text-xs uppercase tracking-wider">
                      {t("auth.personal_info")}
                    </p>
                    
                    {/* Ülke */}
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-white/70 text-sm">
                        {t("auth.country")}
                      </Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <Input
                          id="country"
                          placeholder="Turkiye"
                          value={formData.country}
                          onChange={(e) => updateField("country", e.target.value)}
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        />
                      </div>
                    </div>

                    {/* Şehir */}
                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-white/70 text-sm">
                        {t("auth.city")}
                      </Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <Input
                          id="city"
                          placeholder="Istanbul"
                          value={formData.city}
                          onChange={(e) => updateField("city", e.target.value)}
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        />
                      </div>
                    </div>

                    {/* Uyruk */}
                    <div className="space-y-2">
                      <Label htmlFor="nationality" className="text-white/70 text-sm">
                        {t("auth.nationality")}
                      </Label>
                      <div className="relative">
                        <Flag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <Input
                          id="nationality"
                          placeholder="T.C."
                          value={formData.nationality}
                          onChange={(e) => updateField("nationality", e.target.value)}
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        />
                      </div>
                    </div>

                    {/* Doğum Tarihi */}
                    <div className="space-y-2">
                      <Label htmlFor="dateOfBirth" className="text-white/70 text-sm">
                        {t("auth.date_of_birth")}
                      </Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <Input
                          id="dateOfBirth"
                          type="date"
                          value={formData.dateOfBirth}
                          onChange={(e) => updateField("dateOfBirth", e.target.value)}
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Personel alanları */}
              {accountType === "staff" && (
                <>
                  <Separator className="bg-white/10 my-4" />
                  <div className="space-y-2">
                    <Label htmlFor="invitationCode" className="text-white/70 text-sm flex items-center gap-1">
                      {t("auth.invitation_code")}
                      <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <Input
                        id="invitationCode"
                        placeholder="STAFF-2024-XXXX"
                        value={formData.invitationCode}
                        onChange={(e) => updateField("invitationCode", e.target.value)}
                        required
                        className={`pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 ${
                          invitationValid === true ? "border-green-500/50" : 
                          invitationValid === false ? "border-red-500/50" : ""
                        }`}
                      />
                      {invitationValidating && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-white/50" />
                      )}
                      {invitationValid === true && (
                        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                      )}
                      {invitationValid === false && (
                        <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <p className="text-xs text-white/40">{t("auth.invitation_required")}</p>
                  </div>
                </>
              )}

              <Separator className="bg-white/10 my-4" />

              {/* Şifre */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/70 text-sm">
                  {t("auth.password")}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => updateField("password", e.target.value)}
                    required
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
              </div>

              {/* Şifre Tekrar */}
              <div className="space-y-2">
                <Label htmlFor="passwordConfirm" className="text-white/70 text-sm">
                  {t("auth.password_confirm")}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    id="passwordConfirm"
                    type="password"
                    placeholder="••••••••"
                    value={formData.passwordConfirm}
                    onChange={(e) => updateField("passwordConfirm", e.target.value)}
                    required
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
              </div>

              {/* Hata mesajı */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              {/* Kayıt butonu */}
              <Button
                type="submit"
                disabled={loading || (accountType === "staff" && !invitationValid)}
                className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-white font-medium h-11 text-sm transition-all duration-300 hover:shadow-lg hover:shadow-[#D4AF37]/20"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    {t("auth.register")}
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-white/50 text-sm mt-4">
              {t("auth.has_account")}{" "}
              <Link to={`/${lang}/giris`} className="text-[#D4AF37] hover:text-[#B5952F]">
                {t("auth.login")}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
      <Toaster />
    </div>
  );
}
